import { useState } from 'react'
import { type AlternativesVotingProps, type AlternativeProposalFormData } from '../../../types/alternatives-voting'
import { AlternativeItem } from './AlternativeItem'
import { Plus, X, Sparkles, AlertCircle } from 'lucide-react'

export function AlternativesVoting({
  foreignWord,
  alternatives,
  users,
  currentUser: _currentUser,
  userVotes,
  onVote,
  onProposeAlternative,
  onShare,
}: AlternativesVotingProps) {
  const [isProposing, setIsProposing] = useState(false)
  const [formData, setFormData] = useState<AlternativeProposalFormData>({
    egyptianWord: '',
    explanation: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Sort alternatives by net score (highest first)
  const sortedAlternatives = [...alternatives].sort((a, b) => b.netScore - a.netScore)
  const topAlternative = sortedAlternatives[0]

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.egyptianWord.trim()) {
      newErrors.egyptianWord = 'الكلمة المصرية مطلوبة'
    }

    if (!formData.explanation.trim()) {
      newErrors.explanation = 'الشرح مطلوب'
    } else if (formData.explanation.trim().length < 20) {
      newErrors.explanation = 'الشرح لازم يكون 20 حرف على الأقل'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmitProposal = () => {
    if (validateForm()) {
      onProposeAlternative?.(formData)
      setFormData({ egyptianWord: '', explanation: '' })
      setIsProposing(false)
      setErrors({})
    }
  }

  return (
    <div className="min-h-screen px-4 py-8 bg-slate-950 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Word Details Header */}
        <div className="p-8 border bg-gradient-to-br from-slate-900 via-slate-900 to-amber-950/20 rounded-2xl border-slate-800">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="px-4 py-2 text-lg font-bold rounded-full bg-amber-500 text-slate-900">
                {foreignWord.wordArabic}
              </div>
              <div className="text-sm text-slate-400">
                {foreignWord.word}
              </div>
              <div className="px-3 py-1 text-sm rounded-full bg-slate-800 text-slate-300">
                {foreignWord.categoryName}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={onShare}
                className="p-2 transition-colors rounded-lg hover:bg-slate-800"
                title="شارك"
              >
                <svg className="w-5 h-5 text-slate-400 hover:text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
              </button>
            </div>
          </div>

          <h1 className="mb-6 text-3xl font-bold text-white">
            بدائل مصرية لكلمة "{foreignWord.word}"
          </h1>

          <div className="space-y-4 text-slate-300">
            <div>
              <h3 className="mb-2 font-semibold text-amber-500">السياق:</h3>
              <p className="leading-relaxed">{foreignWord.context}</p>
            </div>
            <div>
              <h3 className="mb-2 font-semibold text-amber-500">لماذا نحتاج بديل مصري؟</h3>
              <p className="leading-relaxed">{foreignWord.reasoning}</p>
            </div>
          </div>
        </div>

        {/* Propose Alternative Button */}
        <div className="flex justify-center">
          <button
            onClick={() => setIsProposing(!isProposing)}
            className={`flex items-center gap-2 px-8 py-4 text-lg font-bold rounded-xl shadow-lg transition-all duration-200 ${
              isProposing
                ? 'bg-slate-800 hover:bg-slate-700 text-white border-2 border-slate-700'
                : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 hover:shadow-xl hover:scale-105'
            }`}
          >
            {isProposing ? (
              <>
                <X className="w-5 h-5" />
                <span>إلغاء الاقتراح</span>
              </>
            ) : (
              <>
                <Plus className="w-5 h-5" />
                <span>اقترح بديل جديد</span>
              </>
            )}
          </button>
        </div>

        {/* Proposal Form */}
        {isProposing && (
          <div className="p-6 border shadow-lg bg-slate-900 rounded-xl border-slate-800">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-500/10">
                <Sparkles className="w-5 h-5 text-amber-500" />
              </div>
              <h2 className="text-xl font-bold text-white">
                اقتراح بديل مصري جديد
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block mb-2 text-sm font-semibold text-white">
                  الكلمة أو العبارة المصرية المقترحة <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.egyptianWord}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, egyptianWord: e.target.value }))
                    if (errors.egyptianWord) setErrors({ ...errors, egyptianWord: '' })
                  }}
                  placeholder="مثال: علامة تجارية"
                  className={`w-full px-4 py-3 rounded-xl border-2 ${
                    errors.egyptianWord
                      ? 'border-rose-500'
                      : 'border-slate-700'
                  } bg-slate-950 text-white placeholder-slate-500 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 outline-none transition-all`}
                />
                {errors.egyptianWord && (
                  <div className="flex items-center gap-2 mt-2 text-rose-500">
                    <AlertCircle className="w-4 h-4" />
                    <p className="text-sm font-medium">{errors.egyptianWord}</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-semibold text-white">
                  شرح الاقتراح ولماذا هو مناسب <span className="text-rose-500">*</span>
                </label>
                <textarea
                  value={formData.explanation}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, explanation: e.target.value }))
                    if (errors.explanation) setErrors({ ...errors, explanation: '' })
                  }}
                  placeholder="اشرح لماذا هذا البديل مناسب وكيف يعبر عن المعنى المطلوب..."
                  rows={4}
                  className={`w-full px-4 py-3 rounded-xl border-2 ${
                    errors.explanation
                      ? 'border-rose-500'
                      : 'border-slate-700'
                  } bg-slate-950 text-white placeholder-slate-500 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 outline-none transition-all resize-none`}
                />
                {errors.explanation && (
                  <div className="flex items-center gap-2 mt-2 text-rose-500">
                    <AlertCircle className="w-4 h-4" />
                    <p className="text-sm font-medium">{errors.explanation}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSubmitProposal}
                  className="flex-1 px-6 py-3 font-bold transition-all bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 rounded-xl hover:scale-105"
                >
                  إرسال الاقتراح
                </button>
                <button
                  onClick={() => {
                    setIsProposing(false)
                    setFormData({ egyptianWord: '', explanation: '' })
                    setErrors({})
                  }}
                  className="px-6 py-3 font-medium text-white transition-all border-2 bg-slate-800 hover:bg-slate-700 rounded-xl border-slate-700"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Alternatives List */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-center text-white">
            البدائل المقترحة ({sortedAlternatives.length})
          </h2>

          {sortedAlternatives.length === 0 ? (
            // Empty State
            <div className="flex flex-col items-center justify-center py-20 border bg-slate-900 rounded-xl border-slate-800">
              <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-slate-800">
                <Sparkles className="w-8 h-8 text-slate-600" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-slate-300">
                لا توجد بدائل حتى الآن
              </h3>
              <p className="max-w-md mb-6 text-center text-slate-500">
                كن أول من يقترح بديل مصري لهذه الكلمة!
              </p>
              <button
                onClick={() => setIsProposing(true)}
                className="flex items-center gap-2 px-6 py-3 font-bold transition-all bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900 rounded-xl hover:scale-105"
              >
                <Plus className="w-5 h-5" />
                <span>اقترح البديل الأول</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedAlternatives.map((alternative, index) => {
                const proposer = users.find((u) => u.id === alternative.proposedBy)
                const isWinner = alternative.id === topAlternative?.id

                return (
                  <AlternativeItem
                    key={alternative.id}
                    alternative={alternative}
                    proposer={proposer}
                    userVote={userVotes[alternative.id]}
                    isWinner={isWinner}
                    rank={index + 1}
                    onVote={onVote}
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
